import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { COMISION_GROUPS } from "./data";
import { useContent } from "@/lib/useContent";
import { Quote } from "lucide-react";

const avatar = (name) =>
  `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(name)}&backgroundColor=eef1f7,e7ecf6&radius=12`;

export const Comision = () => {
  const items = useContent("comision", null);

  const groups = useMemo(() => {
    if (items && items.length) {
      const map = new Map();
      items.forEach((m) => {
        const key = m.group || "Comisión";
        if (!map.has(key)) map.set(key, { id: key, label: key, members: [] });
        map.get(key).members.push(m);
      });
      return Array.from(map.values());
    }
    return COMISION_GROUPS;
  }, [items]);

  const [active, setActive] = useState(null);
  const [flipped, setFlipped] = useState(null);
  const group = groups.find((g) => g.id === active) || groups[0];
  if (!group) return null;

  return (
    <section id="comision" className="relative py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <span className="section-tag">Comisión Directiva</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy">
            Las personas que dirigen el club
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Dirigentes que trabajan ad honorem por la institución. Tocá o pasá el cursor sobre cada uno para ver su rol.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2.5">
          {groups.map((g) => (
            <button
              key={g.id}
              data-testid={`comision-tab-${g.id}`}
              onClick={() => { setActive(g.id); setFlipped(null); }}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                group.id === g.id ? "bg-navy text-white shadow-md" : "bg-[#eef1f7] text-navy hover:bg-navy/10 border border-gray-100"
              }`}
            >
              {g.label}
              <span className={`ml-2 text-xs ${group.id === g.id ? "text-gold" : "text-gray-400"}`}>{g.members.length}</span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={group.id}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4"
          >
            {group.members.map((m, i) => (
              <div
                key={m.id || m.name}
                data-testid={`comision-card-${i}`}
                onClick={() => setFlipped(flipped === i ? null : i)}
                className={`flip h-52 sm:h-56 cursor-pointer ${flipped === i ? "is-flipped" : ""}`}
              >
                <div className="flip-inner">
                  <div className="flip-face bg-[#eef1f7] border border-gray-100 flex flex-col items-center justify-center text-center p-4">
                    <img
                      src={m.image_url || avatar(m.name)}
                      alt={m.name}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = avatar(m.name); }}
                      className="h-20 w-20 rounded-2xl bg-white border border-gray-100 object-cover"
                    />
                    <h3 className="mt-3 text-sm font-bold text-navy leading-tight">{m.name}</h3>
                    <p className="mt-1 text-[11px] font-semibold text-gold uppercase tracking-wide leading-tight">{m.role}</p>
                  </div>
                  <div className="flip-face flip-back bg-navy text-white flex flex-col justify-center p-5">
                    <Quote className="h-6 w-6 text-gold" />
                    <p className="mt-2 text-xs font-bold uppercase tracking-wider text-gold">{m.role}</p>
                    <p className="mt-1.5 text-sm text-white/85 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
